import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { CONFIG_UNIVERSAL } from './floorPlanConfigs';

/**
 * Universal High-Fidelity Floor Plan Heatmap
 * 
 * Logic is unified across ALL TinkerersLabs to ensure "Proper" hotspots and 
 * high-fidelity visuals everywhere.
 */

// ============================================================================
// UNIVERSAL MACHINE POSITIONS (Shared by all TLs for consistent "Proper" Heatmap)
// ============================================================================
export const FLOOR_PLAN_MACHINES = {
    'cnc': { id: 617, name: 'CNC Router', x: 25.0, y: 20.0, zone: 'MACHINE_SHOP' },
    'laser': { id: 618, name: 'Laser Cutter', x: 50.0, y: 10.0, zone: 'MACHINE_SHOP' },
    'drill': { id: 619, name: 'Drill Press', x: 67.5, y: 10.0, zone: 'MACHINE_SHOP' },
    'saw': { id: 620, name: 'Band Saw', x: 80.0, y: 10.0, zone: 'MACHINE_SHOP' },
    'grinder1': { id: 621, name: 'Bench Grinder 1', x: 90.0, y: 10.0, zone: 'MACHINE_SHOP' },
    'grinder2': { id: 622, name: 'Bench Grinder 2', x: 95.0, y: 16.0, zone: 'MACHINE_SHOP' },
    'sander': { id: 623, name: 'Sander', x: 95.0, y: 20.0, zone: 'MACHINE_SHOP' },
    'e_workbench1': { id: 629, name: 'Electronics Workbench 1', x: 15.0, y: 56.0, zone: 'ELECTRONICS' },
    'e_workbench2': { id: 630, name: 'Electronics Workbench 2', x: 15.0, y: 50.0, zone: 'ELECTRONICS' },
    'e_workbench3': { id: 631, name: 'Electronics Workbench 3', x: 20.0, y: 56.0, zone: 'ELECTRONICS' },
    'e_workbench4': { id: 632, name: 'Electronics Workbench 4', x: 20.0, y: 50.0, zone: 'ELECTRONICS' },
    'h_table1': { id: 635, name: 'Heavy Table 1', x: 45.0, y: 56.0, zone: 'WORKBENCHES' },
    'h_table2': { id: 636, name: 'Heavy Table 2', x: 62.5, y: 56.0, zone: 'WORKBENCHES' },
    'printer1': { id: 638, name: '3D Printer 1', x: 92.5, y: 45.0, zone: 'THREE_D_PRINTING' },
    'printer2': { id: 639, name: '3D Printer 2', x: 92.5, y: 50.0, zone: 'THREE_D_PRINTING' },
    'printer3': { id: 640, name: '3D Printer 3', x: 92.5, y: 55.0, zone: 'THREE_D_PRINTING' },
    'printer4': { id: 641, name: '3D Printer 4', x: 92.5, y: 60.0, zone: 'THREE_D_PRINTING' },
    'filament': { id: 642, name: 'Filament Rack', x: 87.5, y: 64.0, zone: 'THREE_D_PRINTING' },
    'conf_table': { id: 645, name: 'Conference Table', x: 75.0, y: 84.0, zone: 'CONFERENCE' },
};

// ============================================================================
// UNIVERSAL ZONE BOUNDARIES (Shared by all TLs)
// ============================================================================
export const FLOOR_PLAN_ZONE_BOUNDS = {
    'MACHINE_SHOP': { label: 'Machine Shop', icon: '⚙️', minX: 0, maxX: 100, minY: 0, maxY: 40, color: 'rgba(255, 152, 0, 0.15)' },
    'ELECTRONICS': { label: 'Electronics', icon: '⚡', minX: 0, maxX: 30, minY: 40, maxY: 70, color: 'rgba(33, 150, 243, 0.15)' },
    'WORKBENCHES': { label: 'Workbenches', icon: '🛠️', minX: 30, maxX: 75, minY: 40, maxY: 70, color: 'rgba(76, 175, 80, 0.15)' },
    'THREE_D_PRINTING': { label: 'Digi-Fab', icon: '🖨️', minX: 75, maxX: 100, minY: 40, maxY: 70, color: 'rgba(156, 39, 176, 0.15)' },
    'ADMIN': { label: 'Admin Desk', icon: '📋', minX: 0, maxX: 50, minY: 70, maxY: 100, color: 'rgba(233, 30, 99, 0.15)' },
    'CONFERENCE': { label: 'Meeting Room', icon: '🤝', minX: 50, maxX: 100, minY: 70, maxY: 100, color: 'rgba(0, 150, 136, 0.15)' },
};

// ============================================================================
// HELPERS
// ============================================================================

const getHeatColor = (intensity, alpha = 0.6) => {
    const t = Math.max(0, Math.min(1, intensity));
    let r, g, b;
    if (t < 0.25) {
        const p = t / 0.25;
        r = 255; g = Math.round(200 - 60 * p); b = Math.round(60 * (1 - p));
    } else if (t < 0.5) {
        const p = (t - 0.25) / 0.25;
        r = 255; g = Math.round(140 - 60 * p); b = 0;
    } else if (t < 0.75) {
        const p = (t - 0.5) / 0.25;
        r = 255; g = Math.round(80 - 80 * p); b = 0;
    } else {
        const p = (t - 0.75) / 0.25;
        r = Math.round(255 - 55 * p); g = 0; b = 0;
    }
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const buildMachineMatchMap = (liveMachines, floorPlanMachines) => {
    if (!liveMachines || !liveMachines.length) return {};
    const matchMap = {};
    const byId = {};
    const byNormName = {};
    liveMachines.forEach(m => {
        byId[m.id] = m;
        const norm = (m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        if (norm) byNormName[norm] = m;
    });

    Object.entries(floorPlanMachines).forEach(([key, fpMachine]) => {
        if (byId[fpMachine.id]) { matchMap[key] = byId[fpMachine.id]; return; }
        const fpNorm = fpMachine.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (byNormName[fpNorm]) { matchMap[key] = byNormName[fpNorm]; return; }
        for (const m of liveMachines) {
            const apiLower = (m.name || '').toLowerCase();
            const fpLower = fpMachine.name.toLowerCase();
            if (apiLower.includes(fpLower) || fpLower.includes(apiLower)) {
                matchMap[key] = m;
                return;
            }
        }
    });
    return matchMap;
};

export const countUsersPerZone = (liveUsers, floorPlanZoneBounds) => {
    const counts = {};
    Object.keys(floorPlanZoneBounds).forEach(k => { counts[k] = 0; });
    (liveUsers || []).forEach(user => {
        const x = parseFloat(user.x);
        const y = parseFloat(user.y);
        for (const [zoneKey, zone] of Object.entries(floorPlanZoneBounds)) {
            if (x >= zone.minX && x <= zone.maxX && y >= zone.minY && y <= zone.maxY) {
                counts[zoneKey]++;
                break;
            }
        }
    });
    return counts;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ImageFloorPlanHeatmap = ({
    liveUsers = [],
    liveMachines = [],
    machineVisits = {},
    showHeatmap = true,
    showMachines = true,
    showUsers = true,
    showZoneLabels = true,
    showDensityHeatmap = true,
    onMachineClick,
    onZoneClick,
    floorPlanImage = '/makerspace_floorplan.png',
    // Site-specific layout config — defaults to the universal template
    floorPlanMachines = CONFIG_UNIVERSAL.machines,
    floorPlanZoneBounds = CONFIG_UNIVERSAL.zoneBounds,
}) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const [imageDims, setImageDims] = useState({ width: 0, height: 0 });

    const syncImageDims = useCallback(() => {
        if (imgRef.current) {
            setImageDims({ width: imgRef.current.clientWidth, height: imgRef.current.clientHeight });
        }
    }, []);

    useEffect(() => {
        const el = imgRef.current;
        if (!el) return;
        const obs = new ResizeObserver(syncImageDims);
        obs.observe(el);
        return () => obs.disconnect();
    }, [syncImageDims]);

    const machineMatchMap = React.useMemo(() => buildMachineMatchMap(liveMachines, floorPlanMachines), [liveMachines, floorPlanMachines]);

    const seededRandom = useCallback((seed) => {
        let s = Math.abs(seed) || 1;
        return () => {
            s = (s * 16807 + 0) % 2147483647;
            return (s - 1) / 2147483646;
        };
    }, []);

    const hashSeed = useCallback((val) => {
        if (typeof val === 'number' && !isNaN(val)) return val;
        const str = String(val || 'default');
        let h = 0;
        for (let i = 0; i < str.length; i++) {
            h = ((h << 5) - h + str.charCodeAt(i)) | 0;
        }
        return Math.abs(h) || 1;
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !showDensityHeatmap || !showHeatmap) return;
        const { width, height } = imageDims;
        if (width === 0 || height === 0) return;

        canvas.width = width * 2; canvas.height = height * 2;
        const ctx = canvas.getContext('2d');
        ctx.scale(2, 2); ctx.clearRect(0, 0, width, height);

        const scale = Math.max(width, height);

        const drawHeatNode = (cx, cy, intensity, radius) => {
            ctx.save();
            const alpha = Math.min(0.85, 0.3 + intensity * 0.5);
            const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
            grad.addColorStop(0, getHeatColor(intensity, alpha));
            grad.addColorStop(0.4, getHeatColor(intensity * 0.7, alpha * 0.5));
            grad.addColorStop(1, getHeatColor(0, 0));
            ctx.fillStyle = grad;
            ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2); ctx.fill();
            ctx.restore();
        };

        const usersPerMachine = {};
        liveUsers.forEach(u => { if (u.current_machine) usersPerMachine[u.current_machine] = (usersPerMachine[u.current_machine] || 0) + 1; });

        Object.entries(floorPlanMachines).forEach(([key, machine]) => {
            const api = machineMatchMap[key];
            const density = usersPerMachine[machine.name] || usersPerMachine[api?.name] || 0;
            if (density > 0) {
                const isHot = density >= 5;
                drawHeatNode((machine.x / 100) * width, (machine.y / 100) * height, isHot ? 1.0 : 0.4 + (density / 5) * 0.4, isHot ? scale * 0.08 : scale * 0.05);
            }
        });

        liveUsers.forEach((user) => {
            const x = parseFloat(user.x) / 100 * width; const y = parseFloat(user.y) / 100 * height;
            drawHeatNode(x, y, 0.5, scale * 0.04);
        });

    }, [liveUsers, liveMachines, machineVisits, imageDims, showDensityHeatmap, showHeatmap, machineMatchMap, floorPlanMachines, seededRandom, hashSeed]);

    const zoneUserCounts = React.useMemo(() => countUsersPerZone(liveUsers, floorPlanZoneBounds), [liveUsers, floorPlanZoneBounds]);

    const getZoneHeat = useCallback((zoneKey) => {
        const n = zoneUserCounts[zoneKey] || 0;
        return n === 0 ? 0 : Math.min(0.15 + n * 0.22, 1);
    }, [zoneUserCounts]);

    const getVisitIntensity = useCallback((machineKey) => {
        const fp = floorPlanMachines[machineKey];
        const api = machineMatchMap[machineKey];
        const visits = Math.max(machineVisits[fp?.id] || 0, machineVisits[api?.id] || 0);
        const maxV = Math.max(...Object.values(machineVisits), 10);
        return Math.min(visits / maxV, 1);
    }, [machineVisits, machineMatchMap, floorPlanMachines]);

    const getMachineStatus = useCallback((machineKey) => {
        const api = machineMatchMap[machineKey];
        return { isActive: api?.status === 'active', load: api?.load || 0, name: api?.name || null, apiId: api?.id || null };
    }, [machineMatchMap]);



    return (
        <Box ref={containerRef} sx={{ position: 'relative', width: '100%', margin: '0 auto', bgcolor: '#f8f9fa', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <img ref={imgRef} src={floorPlanImage} alt="Makerspace" onLoad={syncImageDims} style={{ width: '100%', height: 'auto', display: 'block' }} onError={(e) => { e.target.src = '/floor_plan.png'; }} />
            {showDensityHeatmap && showHeatmap && <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 5 }} />}
            {showHeatmap && Object.entries(floorPlanZoneBounds).map(([zoneKey, zone]) => {
                const heat = getZoneHeat(zoneKey);
                const count = zoneUserCounts[zoneKey] || 0;
                return (
                    <Box key={`zone-${zoneKey}`} onClick={() => onZoneClick?.(zoneKey, zone)} sx={{ position: 'absolute', left: `${zone.minX}%`, top: `${zone.minY}%`, width: `${zone.maxX - zone.minX}%`, height: `${zone.maxY - zone.minY}%`, border: '1px dashed rgba(0,0,0,0.05)', borderRadius: '4px', cursor: onZoneClick ? 'pointer' : 'default', zIndex: 2 }}>
                        {count > 0 && <Box sx={{ position: 'absolute', top: 3, right: 3, bgcolor: `rgba(220, ${Math.round(40 - heat * 40)}, 0, 0.9)`, color: '#fff', fontSize: '9px', fontWeight: 800, px: 0.6, py: 0.15, borderRadius: '6px', minWidth: '16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', lineHeight: 1.3, zIndex: 6 }}>{count}</Box>}
                    </Box>
                );
            })}
            {showMachines && Object.entries(floorPlanMachines).map(([mKey, machine]) => {
                const st = getMachineStatus(mKey);
                const vi = getVisitIntensity(mKey);
                const displayName = st.name || machine.name;
                const visits = Math.max(machineVisits[machine.id] || 0, machineVisits[st.apiId] || 0);
                return (
                    <Tooltip key={`mk-${mKey}`} arrow placement="top" title={<Box sx={{ p: 0.3 }}><Typography sx={{ fontWeight: 600, fontSize: '12px' }}>{displayName}</Typography><Typography sx={{ fontSize: '10px', color: '#aaa' }}>{machine.zone}</Typography>{st.isActive ? <Typography sx={{ fontSize: '10px', color: '#4caf50' }}>● Active &bull; {st.load}% Load</Typography> : <Typography sx={{ fontSize: '10px', color: '#999' }}>○ Idle</Typography>}{visits > 0 && <Typography sx={{ fontSize: '10px', color: '#ff9800' }}>Visits: {visits}</Typography>}</Box>}>
                        <Box onClick={() => onMachineClick?.(mKey, machine)} sx={{ position: 'absolute', left: `${machine.x}%`, top: `${machine.y}%`, width: '10px', height: '10px', transform: 'translate(-50%, -50%)', backgroundColor: st.isActive ? '#4caf50' : '#bdbdbd', border: '2px solid rgba(255,255,255,0.9)', borderRadius: '3px', cursor: 'pointer', boxShadow: st.isActive ? '0 1px 6px rgba(76,175,80,0.4)' : '0 1px 3px rgba(0,0,0,0.15)', transition: 'all 0.2s ease', zIndex: 7, opacity: 0.7, '&:hover': { transform: 'translate(-50%, -50%) scale(1.4)', opacity: 1, zIndex: 100 } }} />
                    </Tooltip>
                );
            })}
            {showUsers && liveUsers.map((user, idx) => (
                <Tooltip key={user.id || `u-${idx}`} arrow title={<Box><Typography sx={{ fontWeight: 700, fontSize: '12px' }}>{user.name || `User ${user.id}`}</Typography>{user.current_machine && <Typography sx={{ fontSize: '10px', color: '#81c784' }}>Using: {user.current_machine}</Typography>}</Box>}>
                    <Box sx={{ position: 'absolute', left: `${user.x}%`, top: `${user.y}%`, display: 'flex', flexDirection: 'column', alignItems: 'center', transform: 'translate(-50%, -50%)', transition: 'left 1.8s ease-out, top 1.8s ease-out', zIndex: 100 }}>
                        <Box sx={{ position: 'absolute', width: '22px', height: '22px', borderRadius: '50%', border: '2px solid rgba(33,150,243,0.3)', animation: 'userRing 2s infinite', '@keyframes userRing': { '0%': { transform: 'scale(0.8)', opacity: 0.8 }, '100%': { transform: 'scale(1.6)', opacity: 0 } } }} />
                        <Box sx={{ width: '12px', height: '12px', bgcolor: '#1e88e5', border: '2px solid #fff', borderRadius: '50%', boxShadow: '0 2px 8px rgba(30,136,229,0.5)', position: 'relative', zIndex: 1 }} />
                        <Typography sx={{ mt: 0.35, fontSize: '7px', fontWeight: 700, color: '#333', bgcolor: 'rgba(255,255,255,0.92)', px: 0.5, py: 0.15, borderRadius: '6px', whiteSpace: 'nowrap', maxWidth: '55px', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>{user.name || `U${user.id}`}</Typography>
                    </Box>
                </Tooltip>
            ))}
            {showZoneLabels && Object.entries(floorPlanZoneBounds).map(([zoneKey, zone]) => (
                <Typography key={`lbl-${zoneKey}`} sx={{ position: 'absolute', left: `${(zone.minX + zone.maxX) / 2}%`, top: `${(zone.minY + zone.maxY) / 2}%`, transform: 'translate(-50%, -50%)', fontSize: '9px', fontWeight: 700, color: 'rgba(0,0,0,0.32)', textTransform: 'uppercase', letterSpacing: '0.5px', pointerEvents: 'none', textShadow: '0 1px 2px rgba(255,255,255,0.9)', whiteSpace: 'nowrap', zIndex: 3 }}>
                    {zone.label}
                </Typography>
            ))}
        </Box>
    );
};

export default ImageFloorPlanHeatmap;
